import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { NAV } from '@/config/nav';
import { useAuth } from '@/hooks/useAuth';
import {
  ChevronDown, ChevronsLeft, ChevronsRight,
  LogOut, Sparkles, Settings as SettingsIcon, Database,
  Stethoscope, Activity, Briefcase, Package, LifeBuoy, UtensilsCrossed,
  Coffee, Fan, Handshake, ShieldAlert, BookOpen, GraduationCap,
  Plus, Smile, Pill, RefreshCw, Receipt, Star, CalendarCheck, MapPin,
  Award, ShoppingBag, Home, Eye, FileSignature, CreditCard, Dumbbell,
  User, IdCard, Scissors, BedDouble, Car, Compass, PackageCheck, Scale,
  Clock, Gift, PartyPopper, Tag, Plane, Ticket,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { BrandMark } from '@/components/brand/BrandMark';
import toast from 'react-hot-toast';
import {
  customObjectsService,
  CustomObjectType,
} from '@/services/custom-objects.service';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { modulesForPath } from '@/lib/module-access';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Lucide icons available to custom object types — keep this synced with
// templates.config.ts on the backend. Unknown names fall back to Database.
const CUSTOM_OBJECT_ICONS: Record<string, LucideIcon> = {
  Database, Stethoscope, Activity, Briefcase, Package, LifeBuoy,
  UtensilsCrossed, Coffee, Fan, Handshake, ShieldAlert, BookOpen, GraduationCap,
  Smile, Pill, RefreshCw, Receipt, Star, CalendarCheck, MapPin,
  Award, ShoppingBag, Home, Eye, FileSignature, CreditCard, Dumbbell,
  User, IdCard, Scissors, BedDouble, Car, Compass, PackageCheck, Scale,
  Clock, Gift, PartyPopper, Tag, Plane, Ticket, Sparkles,
};

const STORAGE_KEY = 'slightor_sidebar_expanded';
const DATA_GROUP_KEY = '__data__';

const readExpanded = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const Sidebar: React.FC = () => {
  const { user, profile, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();

  const { has, isPlatformOwner, loading: modulesLoading } = useModuleAccess();
  const customObjectsEnabled = has('custom_objects');

  // Custom object types — dynamic sidebar group
  const [customTypes, setCustomTypes] = useState<CustomObjectType[]>([]);
  useEffect(() => {
    if (!customObjectsEnabled) { setCustomTypes([]); return; }
    let cancelled = false;
    customObjectsService
      .listTypes(false)
      .then((t) => { if (!cancelled) setCustomTypes(t); })
      .catch(() => { /* silent — sidebar should never break the app */ });
    return () => { cancelled = true; };
  }, [customObjectsEnabled]);

  // Page-level switches + the company's own words come from its industry blueprint.
  const { pageOn, label } = useWorkspace();
  const visibleNav = useMemo(
    () => NAV
      .filter((g) => g.moduleKey !== 'products' || isPlatformOwner)
      .map((g) => ({
        ...g,
        title: g.labelKey ? label(g.labelKey) : g.group,
        items: g.items
          .filter((i) => (!i.adminOnly || user?.role === 'company_admin') && (!i.page || pageOn(i.page)) && modulesForPath(i.to).every(alternatives => alternatives.some(has)))
          .map((i) => (i.labelKey ? { ...i, label: label(i.labelKey) } : i)),
      }))
      .filter((g) => g.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [has, isPlatformOwner, pageOn, label, user?.role],
  );

  // Which group contains the active route — used to auto-expand on navigation
  const activeGroup = useMemo(() => {
    if (pathname.startsWith('/custom-objects')) return DATA_GROUP_KEY;
    for (const g of NAV) {
      if (g.items.some((i) => !i.comingSoon && (pathname === i.to || pathname.startsWith(i.to + '/')))) {
        return g.group;
      }
    }
    return null;
  }, [pathname]);

  // ── Auto-collapse on workspace routes ─────────────────────────────────────
  // Campaign workspaces (/facebook/workspace, /instagram/workspace,
  // /tiktok/workspace) carry their own left rail, so the global sidebar
  // collapses to a slim strip there to give the builder room. The user can
  // re-expand (or collapse anywhere) — the override lasts until the route
  // switches between workspace and non-workspace, then the rule reapplies.
  const isWorkspaceRoute = /workspace/i.test(pathname);
  const [narrowScreen, setNarrowScreen] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const update = () => setNarrowScreen(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  // On a phone the 256 px sidebar left pages ~120 px wide (it used to collapse only on the WhatsApp
  // template/campaign pages): phones start on the slim rail, the opened sidebar floats over the page,
  // and every navigation folds it away again.
  const [collapseOverride, setCollapseOverride] = useState<boolean | null>(null);
  useEffect(() => { setCollapseOverride(null); }, [isWorkspaceRoute, narrowScreen]);
  useEffect(() => { if (narrowScreen) setCollapseOverride(null); }, [pathname, narrowScreen]);
  const collapsed = collapseOverride ?? (isWorkspaceRoute || narrowScreen);

  // Persisted expanded state — undefined means "fall back to active-group rule"
  const [expanded, setExpanded] = useState<Record<string, boolean>>(readExpanded);

  const isExpanded = (key: string) =>
    expanded[key] !== undefined ? expanded[key] : key === activeGroup;

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !isExpanded(key) };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const initials = profile
    ? `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase() || '·'
    : (user?.role === 'super_admin' ? 'SA' : '·');
  const fullName = profile ? `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() : '';
  const email = profile?.email ?? '';

  // Slim rail — logo + expand control only. Rendered on workspace routes
  // (or when the user collapses manually) so the campaign builder gets the
  // width while the app nav stays one click away.
  if (collapsed) {
    return (
      <aside className="relative flex h-screen w-14 shrink-0 flex-col items-center border-r border-border bg-surface py-4">
        <button
          type="button"
          onClick={() => nav('/dashboard')}
          className="relative h-9 w-9 rounded-xl bg-[#1A1A1A]"
          title="Slightor — Dashboard"
        >
          <span className="absolute inset-0 flex items-center justify-center">
            <BrandMark size={20} />
          </span>
        </button>
        <button
          type="button"
          onClick={() => setCollapseOverride(false)}
          className="mt-4 flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
          title="Expand sidebar"
        >
          <ChevronsRight size={15} />
        </button>
      </aside>
    );
  }

  return (
    <aside className={clsx('flex h-screen w-64 flex-col border-r border-border bg-surface', narrowScreen ? 'fixed left-0 top-0 z-50 shadow-elevated' : 'relative')}>
      {/* Brand */}
      <div className="px-4 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="relative h-9 w-9 rounded-xl bg-[#1A1A1A]">
            <span className="absolute inset-0 flex items-center justify-center">
              <BrandMark size={20} />
            </span>
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-[15px] font-bold tracking-tight text-text-primary">Slightor</span>
            <span className="text-2xs text-text-muted tracking-wide">Marketing Suite</span>
          </div>
          <button
            type="button"
            onClick={() => setCollapseOverride(true)}
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
            title="Collapse sidebar"
          >
            <ChevronsLeft size={15} />
          </button>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 pt-1">
        {/* Dynamic Data group — surfaces enabled custom object types */}
        {customObjectsEnabled && <CollapsibleGroup
          label="Data"
          icon={Database}
          expanded={isExpanded(DATA_GROUP_KEY)}
          onToggle={() => toggle(DATA_GROUP_KEY)}
          headerAction={
            <button
              onClick={(e) => { e.stopPropagation(); nav('/custom-objects'); }}
              className="rounded p-0.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
              title="Manage custom object types"
            >
              <SettingsIcon size={11} />
            </button>
          }
        >
          {customTypes.length === 0 ? (
            <button
              onClick={() => nav('/custom-objects')}
              className="sidebar-link group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13.5px] font-medium text-text-muted transition-colors hover:bg-surface-alt"
              title="Install a vertical template (Hospital / Software / Restaurant / etc.)"
            >
              <Plus size={16} className="shrink-0 text-text-muted" />
              <span className="flex-1">Add data type</span>
            </button>
          ) : (
            <>
              {customTypes.map((t) => {
                const Icon = CUSTOM_OBJECT_ICONS[t.icon ?? 'Database'] ?? Database;
                return (
                  <NavLink
                    key={t.key}
                    to={`/custom-objects/${t.key}`}
                    className={({ isActive }) =>
                      clsx(
                        'sidebar-link group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] transition-colors duration-150',
                        isActive
                          ? 'bg-primary-soft font-semibold text-primary'
                          : 'font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={clsx('hidden', isActive ? 'opacity-100' : 'opacity-0')} />
                        <Icon size={16} className={clsx('shrink-0 transition-colors', isActive ? 'text-primary' : 'text-text-muted group-hover:text-text-primary')} />
                        <span className="flex-1 truncate">{t.pluralLabel ?? t.label}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
              <NavLink
                to="/custom-objects"
                className="sidebar-link group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[12.5px] font-medium text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
              >
                <SettingsIcon size={13} className="shrink-0" />
                <span className="flex-1">Manage types</span>
              </NavLink>
            </>
          )}
        </CollapsibleGroup>}

        {modulesLoading && visibleNav.length === 0 && (
          <div className="space-y-1 px-1 py-1" role="status" aria-busy="true" aria-label="Loading menu">
            {[112, 96, 120, 88, 104, 92, 116, 84, 100].map((w, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <span className="skeleton h-4 w-4 rounded" />
                <span className="skeleton h-3.5 rounded" style={{ width: w }} />
                <span className="skeleton ml-auto h-3 w-3 rounded" />
              </div>
            ))}
          </div>
        )}
        {visibleNav.map((g) => {
          const open = isExpanded(g.group);
          const groupHasActive = g.group === activeGroup;
          return (
            <CollapsibleGroup
              key={g.group}
              label={g.title}
              icon={g.icon ?? g.items[0]?.icon}
              expanded={open}
              onToggle={() => toggle(g.group)}
              showActiveDot={!open && groupHasActive}
              inTesting={g.inTesting}
            >
              {g.items.map((item) => {
                // If any sibling item lives under this item's path (e.g.
                // `/tiktok` is the parent of `/tiktok/workspace`), this item
                // is a "section root" and NavLink must use `end` so the
                // active state doesn't bleed onto every child route.
                const isSectionRoot = g.items.some(
                  (sib) => sib !== item && sib.to.startsWith(item.to + '/'),
                );
                if (item.comingSoon) {
                  return (
                    <button
                      key={item.to}
                      onClick={() => toast(`${item.label} — coming soon`, { icon: '🚧' })}
                      className="sidebar-link group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13.5px] font-medium text-text-muted transition-colors hover:bg-surface-alt"
                    >
                      <item.icon size={16} className="shrink-0 text-text-muted" />
                      <span className="flex-1">{item.label}</span>
                      <span className="rounded-full bg-warning-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">
                        Soon
                      </span>
                    </button>
                  );
                }
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={isSectionRoot}
                    className={({ isActive }) =>
                      clsx(
                        'sidebar-link group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] transition-colors duration-150',
                        isActive
                          ? 'bg-primary-soft font-semibold text-primary'
                          : 'font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={clsx(
                            'hidden',
                            isActive ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <item.icon
                          size={16}
                          className={clsx(
                            'shrink-0 transition-colors',
                            isActive ? 'text-primary' : 'text-text-muted group-hover:text-text-primary',
                          )}
                        />
                        <span className="flex-1">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </CollapsibleGroup>
          );
        })}
      </nav>

      {/* User chip */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-alt p-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-[12px] font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold text-text-primary">{fullName || email || 'Account'}</div>
            <div className="truncate text-2xs text-text-muted">{email || (user?.role.replace('_', ' '))}</div>
          </div>
          <button
            onClick={() => { logout(); nav('/login', { replace: true }); }}
            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
};

interface CollapsibleGroupProps {
  label: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
  showActiveDot?: boolean;
  inTesting?: boolean;
}

const CollapsibleGroup: React.FC<CollapsibleGroupProps> = ({
  label, icon: Icon, expanded, onToggle, children, headerAction, showActiveDot, inTesting,
}) => (
  <div className="mb-1">
    {/* The toggle button and the optional action are SIBLINGS, not nested —
        HTML forbids <button> inside <button> (React 19 throws a hydration
        error). The wrapping <div> provides the hover background for both. */}
    <div className="group flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-alt">
      <button
        type="button"
        onClick={onToggle}
        className="sidebar-link flex flex-1 items-center gap-2.5 rounded-md text-left"
        aria-expanded={expanded}
      >
        {Icon && <Icon size={16} className={clsx('shrink-0', expanded ? 'text-primary' : 'text-text-muted group-hover:text-text-primary')} />}
        <span className={clsx('min-w-0 truncate whitespace-nowrap text-[13.5px]', expanded ? 'font-semibold text-text-primary' : 'font-medium text-text-secondary group-hover:text-text-primary')}>
          {label}
        </span>
        {inTesting && (
          <span className="shrink-0 whitespace-nowrap rounded-full bg-warning-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning" title="This module is still being tested">
            In testing
          </span>
        )}
        {showActiveDot && (
          <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
        )}
        <ChevronDown
          size={14}
          className={clsx(
            'ml-auto text-text-muted/40 transition-all duration-150 group-hover:text-text-muted',
            expanded ? 'rotate-0' : '-rotate-90',
          )}
        />
      </button>
      {headerAction && <span className="ml-2 shrink-0">{headerAction}</span>}
    </div>
    {expanded && (
      <div className="mb-2 ml-[21px] mt-1 flex flex-col gap-0.5 border-l border-border/60 pl-2.5">
        {children}
      </div>
    )}
  </div>
);
